import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/MeterAssignmentsService';
import {MeterAssignment} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {buildCreateMeterAssignmentInput, buildUpdateMeterAssignmentInput} from '../services/buildMeterAssignmentInput';
import {meterAssignmentsQueryKeys} from './useMeterAssignments';

export const useMeterAssignment = (meterAssignmentId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const meterAssignmentQuery = useQuery({
        queryKey: meterAssignmentsQueryKeys.detail(meterAssignmentId),
        queryFn: () => fetch(meterAssignmentId!),
        enabled: !!meterAssignmentId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: MeterAssignment) => add(buildCreateMeterAssignmentInput(values)),
        onSuccess: () => {
            notifySuccess('Meter Assignment created');
            // The root key covers the list and every detail entry.
            queryClient.invalidateQueries({queryKey: meterAssignmentsQueryKeys.all});
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: MeterAssignment) => {
            if (!meterAssignmentId) throw new Error('meterAssignmentId is required parameter');
            return update(meterAssignmentId, buildUpdateMeterAssignmentInput(values) as MeterAssignment);
        },
        onSuccess: () => {
            notifySuccess('Meter Assignment updated');
            // The root key covers the list and every detail entry.
            queryClient.invalidateQueries({queryKey: meterAssignmentsQueryKeys.all});
        },
        onError: notifyError
    });

    // DELETE MUTATION — FNP-217: delete by composite key (customerId, sortKey).
    const deleteMutation = useMutation({
        mutationFn: (record: Pick<MeterAssignment, 'customerId' | 'sortKey'>) => {
            return deleteRecord(record);
        },
        onSuccess: () => {
            notifySuccess('Meter Assignment deleted');
            // The root key covers the list and every detail entry.
            queryClient.invalidateQueries({queryKey: meterAssignmentsQueryKeys.all});
        },
        onError: notifyError
    });

    return {
        record: meterAssignmentQuery.data,
        loading: meterAssignmentQuery.isLoading,
        fetchRecord: meterAssignmentQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
